/**
 * An attachment drawn in place: an image, a player, or the start of a text.
 *
 * The bytes come through Cloud's bridge (`lib/relay/cloud-bridge.ts`) and every
 * decision about them from `lib/relay/preview.ts`. Whatever those turn down —
 * no link, a type off the list, a file over its cap, a password, a failure —
 * is the plain card, which is why the card is handed in rather than drawn here.
 *
 * Images and text load as they near the screen, the first four in a message
 * (`autoLoads`); past those they are the card with a button to draw them.
 * Video and sound load when somebody presses play. The name stays under each
 * one and opens the file in Cloud, as the card does.
 *
 * **Text is a string child**, so React escapes it: never HTML, never a blob
 * URL. An image enlarges in a dialog here rather than in a tab of its own, but
 * a blob URL can still be opened as a page — the browser's own menu offers it
 * on an image or a video. That is safe only because `preview.ts` makes a Blob
 * of nothing but the listed image, video and sound types.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { ds } from '../../ds';
import { useT } from '../../i18n';
import { clipText, previewOf, previews, type Preview } from '../../lib/relay/preview';
import type { Attachment } from '../../lib/relay/types';
import './chat.css';

type State = { status: 'idle' } | { status: 'ready'; value: string } | { status: 'failed' };

function stateOf(value: string | null | undefined): State {
  if (value === null) return { status: 'failed' };
  return value === undefined ? { status: 'idle' } : { status: 'ready', value };
}

/** The preview's value, asked for once `start` is true. */
function usePreview(preview: Preview, start: boolean): State {
  const [state, setState] = useState<State>(() => stateOf(previews.peek(preview)));
  const key = `${preview.type} ${preview.link.secret}`;

  useEffect(() => {
    if (!start) return;
    let live = true;
    previews.load(preview).then(
      (value) => live && setState({ status: 'ready', value }),
      () => live && setState({ status: 'failed' })
    );
    return () => {
      live = false;
    };
    // The key is the preview: a fresh object each render says nothing new.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, start]);

  return state;
}

/**
 * True once the element has come within a screen of view, and from then on.
 * The transcript scrolls inside its own box, and a margin applies only to the
 * observer's root, so that box is the root.
 */
function useNear(): [boolean, (el: HTMLElement | null) => void] {
  const [near, setNear] = useState(false);
  const [el, setEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (near || !el) return;
    const observer = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && setNear(true), {
      root: el.closest('.mv-scroll'),
      rootMargin: '600px 0px'
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [el, near]);

  return [near, setEl];
}

/** The name, which opens the file in Cloud, and the size. */
function Caption({ file }: { file: Attachment }) {
  const t = useT();
  return (
    <a
      className="rl-preview-caption"
      href={file.cloudUrl}
      target="_blank"
      rel="noreferrer"
      title={t('conversation.attachment.open')}
    >
      <span className="rl-truncate">{file.name}</span>
      <span className="rl-preview-size">{Math.round(file.bytes / 1024)} kB</span>
    </a>
  );
}

function Lightbox({ file, url, onClose }: { file: Attachment; url: string; onClose: () => void }) {
  const { Dialog } = ds();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The row's right-click opens the message menu, and would open it under
  // this dialog; here the browser's own menu is the useful one.
  return (
    <div onContextMenu={(e) => e.stopPropagation()}>
      <Dialog open position="fixed" title={file.name} onClose={onClose} width="min(1200px, 100%)">
        <img className="rl-preview-full" src={url} alt={file.name} />
      </Dialog>
    </div>
  );
}

/** The card, and a button that draws the preview instead. */
function Held({ card, onShow }: { card: ReactNode; onShow: () => void }) {
  const t = useT();
  return (
    <div className="rl-preview">
      {card}
      <button type="button" className="rl-preview-more" onClick={onShow}>
        {t('conversation.attachment.showPreview')}
      </button>
    </div>
  );
}

type PreviewProps = { file: Attachment; preview: Preview; card: ReactNode; auto: boolean };

function ImagePreview({ file, preview, card, auto }: PreviewProps) {
  const [near, observe] = useNear();
  const [asked, setAsked] = useState(false);
  const state = usePreview(preview, auto ? near : asked);
  const [open, setOpen] = useState(false);

  if (state.status === 'failed') return <>{card}</>;
  if (state.status === 'idle' && !auto && !asked) return <Held card={card} onShow={() => setAsked(true)} />;
  return (
    <div className="rl-preview">
      {state.status === 'ready' ? (
        <button type="button" className="rl-preview-image" onClick={() => setOpen(true)}>
          <img src={state.value} alt={file.name} />
        </button>
      ) : (
        <div ref={observe} className="rl-preview-box" />
      )}
      <Caption file={file} />
      {open && state.status === 'ready' && <Lightbox file={file} url={state.value} onClose={() => setOpen(false)} />}
    </div>
  );
}

function TextPreview({ file, preview, card, auto }: PreviewProps) {
  const t = useT();
  const [near, observe] = useNear();
  const [asked, setAsked] = useState(false);
  const state = usePreview(preview, auto ? near : asked);
  const [all, setAll] = useState(false);

  if (state.status === 'failed') return <>{card}</>;
  if (state.status === 'idle' && !auto && !asked) return <Held card={card} onShow={() => setAsked(true)} />;
  if (state.status === 'idle') {
    return (
      <div className="rl-preview">
        <div ref={observe} className="rl-preview-text rl-preview-text-wait" />
        <Caption file={file} />
      </div>
    );
  }

  const clip = clipText(state.value);
  return (
    <div className="rl-preview">
      <pre className={all ? 'rl-preview-text rl-preview-text-all' : 'rl-preview-text'}>
        {all ? state.value : clip.text}
      </pre>
      {clip.clipped && !all && (
        <button type="button" className="rl-preview-more" onClick={() => setAll(true)}>
          {t('conversation.attachment.showAll')}
        </button>
      )}
      <Caption file={file} />
    </div>
  );
}

function MediaPreview({ file, preview, card }: { file: Attachment; preview: Preview; card: ReactNode }) {
  const { Icon } = ds();
  const t = useT();
  const [started, setStarted] = useState(false);
  const state = usePreview(preview, started);

  if (state.status === 'failed') return <>{card}</>;
  if (state.status === 'ready') {
    return (
      <div className="rl-preview">
        {preview.kind === 'video' ? (
          <video className="rl-preview-video" src={state.value} controls autoPlay={started} />
        ) : (
          <audio className="rl-preview-audio" src={state.value} controls autoPlay={started} />
        )}
        <Caption file={file} />
      </div>
    );
  }
  return (
    <div className="rl-preview rl-preview-player">
      <button
        type="button"
        className="rl-preview-play"
        disabled={started}
        aria-label={t('conversation.attachment.play', { name: file.name })}
        onClick={() => setStarted(true)}
      >
        {started ? (
          <span className="rl-preview-spin">
            <Icon name="Loading" size={16} />
          </span>
        ) : (
          <Icon name={preview.kind === 'video' ? 'Video' : 'SpeakerOn'} size={16} />
        )}
      </button>
      <Caption file={file} />
    </div>
  );
}

/**
 * `file` drawn in place where it can be, and `card` where it cannot. Unless
 * `auto`, an image or a text waits for a click before it loads.
 */
export function AttachmentView({ file, card, auto = true }: { file: Attachment; card: ReactNode; auto?: boolean }) {
  const preview = previewOf(file);
  if (!preview) return <>{card}</>;
  if (preview.kind === 'image') return <ImagePreview file={file} preview={preview} card={card} auto={auto} />;
  if (preview.kind === 'text') return <TextPreview file={file} preview={preview} card={card} auto={auto} />;
  return <MediaPreview file={file} preview={preview} card={card} />;
}
